import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';

import { getAllAgreementsFetch, switchAgreementEnabledFetch } from '../../app/axios/agreementFetches';
import CreateAgreementModal from '../../modal/agreement/CreateAgreementModal';
import { HLButtonStyleType } from '../../shared/components/button/HLButton';
import HLPageTable, {
    HLPageTableFilterConfig,
    HLPageTableRef,
    HLPageTableRowActionButton
} from '../../shared/components/table/HLPageTable';
import { useAppSelector } from '../../shared/hooks/useAppSelector';
import {
    AgreementStructure,
    AgreementView,
    LearningLanguage,
    LearningLanguageNameMap,
    LessonFormat,
    LessonFormatNameMap
} from '../../shared/types/agreement';
import { TeacherRole } from '../../shared/types/teacher';
import { EnabledFilterOptions } from "../../shared/types/general";
import { useTeachersCache } from "../../shared/hooks/useTeachersCache";
import AgreementLessonPriceCell from '../../components/agreement/grid/AgreementLessonPriceCell';

const searchFiltersConfig: HLPageTableFilterConfig<AgreementView>[] = [
    {
        fieldName: 'name',
        label: 'Название',
        operator: 'like',
        type: 'input'
    },
    {
        fieldName: 'learningLanguage',
        label: 'Язык обучения',
        operator: '=',
        type: 'select',
        options: Object.entries(LearningLanguageNameMap).map(([value, label]) => ({
            value,
            label
        }))
    },
    {
        fieldName: 'lessonFormat',
        label: 'Формат занятий',
        operator: '=',
        type: 'select',
        options: Object.entries(LessonFormatNameMap).map(([value, label]) => ({
            value,
            label
        }))
    },
    {
        fieldName: 'telegram',
        label: 'Telegram ученика',
        operator: 'like',
        type: 'input'
    },
    {
        fieldName: 'enabled',
        label: 'Активность',
        operator: 'is',
        type: 'select',
        options: EnabledFilterOptions,
        defaultValue: 'true'
    }
];

const columnDefs: ColDef<AgreementView>[] = [
    {
        field: 'name',
        headerName: 'Название'
    },
    {
        field: 'learningLanguage',
        headerName: 'Язык обучения',
        valueFormatter: (params) => LearningLanguageNameMap[params.value as LearningLanguage],
    },
    {
        field: 'lessonFormat',
        headerName: 'Формат занятий',
        valueFormatter: (params) => LessonFormatNameMap[params.value as LessonFormat]
    },
    {
        field: 'teacherFullName',
        headerName: 'Учитель'
    },
    {
        field: 'teacherRate',
        headerName: 'Ставка учителя',
        valueFormatter: (params) => params.value ? `${params.value} ₽` : '',
    },
];

const lessonPriceColumn: ColDef<AgreementView> = {
    field: 'lessonPrice',
    headerName: 'Стоимость урока',
    cellRenderer: AgreementLessonPriceCell,
};

const defaultColDef: ColDef<AgreementView> = {
    sortable: false,
    filter: false,
    resizable: true,
    flex: 1,
    minWidth: 100
};

const AgreementsPage: React.FC = () => {
    const tableRef = useRef<HLPageTableRef>(null);

    const currentTeacher = useAppSelector(state => state.auth.currentTeacher)
    const {getTeacherOptions} = useTeachersCache();

    const navigate = useNavigate();
    const [isCreateAgreementModalOpen, setIsCreateAgreementModalOpen] = useState(false);

    const handleSuccessCreate = (agreement: AgreementStructure) => navigate(`/agreements/${agreement.id}`, {state: agreement});

    const actionButtonsConfig = [];
    const rowActionButtons: HLPageTableRowActionButton[] = [];

    const handleOpenAgreement = (params: { data?: AgreementView }) => {
        if (params?.data?.id) window.open(`/agreements/${params.data.id}`, '_blank', 'noreferrer');
    };

    const fullSearchFiltersConfig: HLPageTableFilterConfig<AgreementView>[] = useMemo(() => {
        return [
            {
                fieldName: 'teacherId',
                label: 'Учитель',
                operator: '=',
                type: 'select',
                options: getTeacherOptions(),
            },
            ...searchFiltersConfig
        ]
    }, [getTeacherOptions]);

    const columns = useMemo(() => {
        if (currentTeacher?.role === TeacherRole.ADMIN) {
            return [...columnDefs, lessonPriceColumn];
        }
        return columnDefs;
    }, [currentTeacher?.role]);

    if (currentTeacher?.role === TeacherRole.ADMIN) {
        actionButtonsConfig.push({
            label: 'Создать договор',
            onClick: () => setIsCreateAgreementModalOpen(true),
            styleType: HLButtonStyleType.ORANGE
        })

        rowActionButtons.push({
            onClick: async params => {
                if (!params?.data?.id) return

                try {
                    await switchAgreementEnabledFetch(params.data?.id);
                    params.api.refreshInfiniteCache();
                } catch (err) {
                    console.error('Ошибка при переключении статуса договора:', err);
                }
            },
            getLabel: params => params?.data?.enabled ? 'Деактивировать' : 'Активировать'
        })
    }

    return (
        <>
            <HLPageTable
                ref={tableRef}
                title="Договоры"
                columns={columns}
                defaultColDef={defaultColDef}
                filterConfig={fullSearchFiltersConfig}
                fetchData={getAllAgreementsFetch}
                actionButtons={actionButtonsConfig}
                rowActionButtons={rowActionButtons}
                onRowDoubleClick={handleOpenAgreement}
                height={window.innerHeight}
            />
            <CreateAgreementModal
                open={isCreateAgreementModalOpen}
                setOpen={setIsCreateAgreementModalOpen}
                onSuccess={handleSuccessCreate}
            />
        </>
    );
};

export default AgreementsPage;